/**
 * user_device_pins — server-side bcrypt-hash store for cross-device PIN sync.
 *
 * Phase 4 / Plan 02: shipped via migration 0003_phase4_auth_tables.sql.
 *
 * D-15 / RESEARCH § Discretion #8:
 *   Stores `bcrypt(PIN, cost=10)` per (user, device) pair so a driver who
 *   re-enrolls on a new shared tablet doesn't have to re-create their PIN.
 *   Mobile computes the bcrypt locally with `bcryptjs`, caches the hash in
 *   the SQLCipher-encrypted roster (D-12), and pushes the SAME hash to this
 *   table via `POST /v1/users/me/device-pins` (plan 04-06 ships the route).
 *   On a different device, the server returns the cached hash so the new
 *   device can verify the user's existing PIN offline.
 *
 * NOT tenant-scoped:
 *   PINs follow the USER, not a tenant — same rationale as user_devices.
 *   Cross-user safety is enforced at the API layer (req.userId from the
 *   verified JWT). No RLS policy on this table; the route writer keys every
 *   query on `user_id = req.userId`.
 *
 * Anti-pattern (RESEARCH § Anti-Pattern "Don't write to user_devices..."):
 *   user_device_pins is the ONLY table that the device-pins route writes to
 *   directly; user_devices is touched ONLY by the cognitoJwtPlugin's
 *   first-seen flow. Separating the writers means the PIN-sync code never
 *   accidentally races against the first-seen INSERT.
 *
 * Why bcrypt cost 10 and not higher:
 *   Cost 10 = ~80ms on iPhone-class hardware (D-15 accepted target). The
 *   6-digit PIN's 1M-keyspace plus the 5-strikes lockout (D-15) is the
 *   primary brute-force defense; cost 10 is sufficient given the offline-
 *   stolen-device threat model. Bumping cost just raises the lockout-period
 *   cost, not the meaningful attacker cost.
 *
 * UNIQUE (user_id, device_id_hash):
 *   One PIN per (user, device). Updating a PIN is an UPDATE on the existing
 *   row, never a fresh INSERT — plan 04-06's route uses ON CONFLICT
 *   (user_id, device_id_hash) DO UPDATE.
 *
 * Sources:
 *   - CONTEXT D-15 (PIN security + cross-device sync)
 *   - RESEARCH § Discretion #8 (table shape)
 *   - master spec §7 (shared-device PIN flow)
 *   - gsd/DECISIONS_LOG.md DEC-004 (UTC display rule)
 */

import { pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { users } from './users.js';

export const userDevicePins = pgTable(
  'user_device_pins',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    // Same per-user salted SHA-256 shape as user_devices.device_id_hash
    deviceIdHash: text('device_id_hash').notNull(),
    // bcryptjs cost 10 — never the raw PIN
    pinBcryptHash: text('pin_bcrypt_hash').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // One PIN per (user, device) — plan 04-06 UPSERTs on this index
    uniqueIndex('user_device_pins_user_hash_idx').on(t.userId, t.deviceIdHash),
  ],
);

export type UserDevicePin = typeof userDevicePins.$inferSelect;
export type NewUserDevicePin = typeof userDevicePins.$inferInsert;
export const userDevicePinSelectSchema = createSelectSchema(userDevicePins);
export const userDevicePinInsertSchema = createInsertSchema(userDevicePins);
